import { useState } from "react";
import type { FormEvent } from "react";

import type { TeamMemberUpdateVariables } from "../Pages/TeamMember/TeamMemberEdit/TeamMemberUpdateMutation";
import { Input, Textarea } from "../base";
import type { InputProps } from "../base";
import type { TeamMemberEditData } from "../Pages/TeamMember/TeamMemberEdit/TeamMemberEditQuery";
import type { TeamMemberCreateVariables } from "../Pages/TeamMemberNew/TeamMemberCreateMutation";

import * as styles from "./TeamMemberForm.styles";

interface Props {
  onCreate?: (teamMember: TeamMemberCreateVariables) => Promise<void>;
  onUpdate?: (teamMember: TeamMemberUpdateVariables) => Promise<void>;
  teamMember?: TeamMemberEditData["teamMember"];
}

const TeamMemberForm = ({ onCreate, onUpdate, teamMember }: Props) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [formData, setFormData] = useState<
    TeamMemberUpdateVariables | TeamMemberCreateVariables | null
  >(teamMember ?? null);

  const isEditing = !!teamMember;

  const renderHeader = () => {
    if (isEditing) {
      return <h2>Editing: {teamMember?.name}</h2>;
    }

    return <h2>New Team Member</h2>;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSaving(true);

    if (formData) {
      await onCreate?.(formData as TeamMemberCreateVariables);
      await onUpdate?.(formData as TeamMemberUpdateVariables);
    }

    setIsSaving(false);
  };

  const handleChange: InputProps["onChange"] = (name, value) => {
    if (!name) return;

    setFormData({ ...(formData || { id: "" }), [name]: value });
  };

  return (
    <>
      {renderHeader()}
      <form onSubmit={handleSubmit}>
        <Input
          placeholder="Name"
          required
          name="name"
          id="name"
          value={formData?.name}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="text"
          required
          placeholder="Position"
          name="position"
          id="position"
          value={formData?.position ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="tel"
          placeholder="Phone: xxx-xxx-xxxx"
          name="phone"
          id="phone"
          value={formData?.phone ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="email"
          required
          placeholder="Email"
          name="email"
          id="email"
          value={formData?.email ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <Input
          type="number"
          placeholder="Display Order"
          name="order"
          id="order"
          value={formData?.order ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <input
          type="file"
          id="avatar"
          required={!isEditing}
          name="avatar"
          onChange={({ target: { validity, files } }) => {
            if (validity.valid) {
              setFormData({
                ...(formData || { id: "" }),
                avatar: files?.[0] as File,
              });
            }
          }}
        />
        <Textarea
          placeholder="Description"
          id="description"
          name="description"
          value={formData?.description ?? ""}
          onChange={handleChange}
          errorMessages={{}}
        />
        <button
          css={styles.submitButton}
          className="btn btn-large"
          type="submit"
          name="button"
          disabled={isSaving}
        >
          Send
        </button>
      </form>
    </>
  );
};

export { TeamMemberForm };
